import React, { useEffect, useRef, useState } from "react";
import {
  CardProduct,
  CardProductName,
  ProductInfo,
  Row,
  Price,
  Icons,
  ImageContainer,
  StyledInfinityScroll,
  Subtitle,
  IconContainer,
  SortContainer,
  SortName,
  SectionTitle,
  MainContainer,
  HeaderWrapper,
  Content,
  NothingToShowContainer,
  LoadingContainer,
  LoaderContainer,
  ClickContainer,
} from "./SearchPage.styles.js";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import UseAnimations from "react-useanimations";
import trash2 from "react-useanimations/lib/trash2";
import heart from "react-useanimations/lib/heart";
import {
  resetEditItemData,
  resetSelectedItem,
  toggleFavoriteItem,
} from "../../../store/slices/marketplace/marketplaceSlice";
import { deleteMarketplaceItemLoader } from "../../../store/slices/marketplace/marketplaceSlice.js";
import { selectBusinessUserInfo } from "../../../store/slices/businessUser/slice";
import { checkPermission, priceFormatter } from "../../../utils/utils";
import ModalConfirm from "../../../components/ModalConfirm/ModalConfirm";
import { toast } from "react-toastify";
import { ReactComponent as Edit } from "../../../assets/icons/edit-3.svg";
import { ReactComponent as Share } from "../../../assets/icons/share-icon.svg";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../components/NewHeader/NewHeader.js";
import Footer from "../../../components/Footer.js";

import {
  businessUserRoleAction,
  getCurrencySymbol,
} from "../../../utils/constants";

import LoginModal from "../../../components/Modal/LoginModal.js";

import {
  useFavoriteItemMutation,
  useGetCategoriesQuery,
  useGetMarketplaceItemsQuery,
} from "../../../store/slices/marketplace/marketplaceApi.js";
import { api } from "../../../store/api/index.js";
import store from "../../../store/index.js";
import SkeletonMarketplaceLoader from "../../../components/SkeletonLoader/SkeletonMarketplaceLoader/SkeletonMarketplaceLoader.js";
import { removeItem } from "../../../store/slices/marketplace/asyncThunks.js";
import SkeletonMarketplaceSortLoader from "../../../components/SkeletonLoader/SkeletonMarketplaceSortLoader/SkeletonMarketplaceSortLoader.js";
import openToast from "../../../utils/toast.js";
import AppLoader from "../../../components/AppLoader.js";
import SearchComponent from "../../LandingPage/SearchComponent.js";
import { selectedLocale } from "../../../store/slices/common/slice.js";

function ProductCard({ product, itemDetails, removeItem, modal, reverse }) {
  const businessUser = useSelector(selectBusinessUserInfo);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const productId = product.id;
  const [favoriteItem] = useFavoriteItemMutation();
  const { t } = useTranslation();
  const toastId = useRef(null);
  const locale = useSelector(selectedLocale);
  const ar = locale === "ar";

  const handleOnClickEdit = () => {
    const dataToSend = {
      productId: productId,
      action: "edit",
    };
    navigate(`/admin-portal/marketplace/add-an-item/${dataToSend.action}`, {
      state: dataToSend,
    });
  };

  const onShare = (id) => {
    const link = businessUser.loggedIn
      ? `${window.location.origin}/admin-portal/marketplace/item-detail/${id}`
      : `${window.location.origin}/marketplace/${id}`;

    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success(t("link-copied-to-clipboard"));
      })
      .catch((error) => {
        openToast(
          toastId,
          "share-marketplace-item",
          "error",
          t("Failed to copy link")
        );
        console.error("Error copying link to clipboard:", error);
      });
  };

  return (
    <CardProduct $imageUrl={product?.photos[0]?.url}>
      <Icons>
        {checkPermission(businessUser, businessUserRoleAction.delete_item) && (
          <IconContainer>
            <UseAnimations
              animation={trash2}
              size={32}
              wrapperStyle={{ marginTop: -4 }}
              strokeColor="#484888"
              onClick={() => removeItem(product.id)}
            />
          </IconContainer>
        )}
        {checkPermission(businessUser, businessUserRoleAction.put_item) && (
          <IconContainer>
            <Edit width={"auto"} height={27} onClick={handleOnClickEdit} />
          </IconContainer>
        )}

        <IconContainer>
          <Share
            height={24}
            width={"auto"}
            onClick={() => onShare(productId)}
          />
        </IconContainer>
        <IconContainer>
          <UseAnimations
            animation={heart}
            fillColor={businessUser?.loggedIn ? "#484888" : "transparent"}
            strokeColor="#484888"
            reverse={reverse}
            size={32}
            onClick={
              !businessUser?.loggedIn
                ? modal
                : () => {
                    dispatch(
                      toggleFavoriteItem({
                        id: productId,
                        like: !reverse,
                        businessUserId: businessUser.id,
                      })
                    );
                    favoriteItem({ id: productId })
                      .unwrap()
                      .then(() => {})
                      .catch((err) => {
                        openToast(
                          toastId,
                          "add-item-to-fav",
                          "error",
                          t(err.data.message)
                        );
                      });
                  }
            }
          />
        </IconContainer>
      </Icons>
      <ClickContainer onClick={itemDetails} />

      <ProductInfo>
        <CardProductName
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {ar && product?.nameAr ? product?.nameAr : product?.name}
        </CardProductName>
        <Row>
          <Price>
            {t("from")}{" "}
            {priceFormatter(product?.price ?? product?.samplePrice, true, 15)}{" "}
            {getCurrencySymbol(product?.currency)}
          </Price>
          {product?.minQuantity && (
            <Price>
              Min Qty:{" "}
              <Price style={{ fontWeight: 800 }}>{product?.minQuantity}</Price>
            </Price>
          )}
        </Row>
      </ProductInfo>
    </CardProduct>
  );
}

const SearchPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loggedIn, role } = useSelector(selectBusinessUserInfo);
  const deletePending = useSelector(deleteMarketplaceItemLoader);
  const [selectedItem, setSelectedItem] = useState(null);
  const [areYouSureModal, setAreYouSureModal] = useState(false);
  const [loginModal, setLoginModal] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchTerm = queryParams.get("searchTerm");
  const toastId = useRef(null);

  const [queryData, setQueryData] = useState({
    page: 1,
    category: "",
    search: searchTerm || "",
  });

  useEffect(() => {
    if (loggedIn) {
      if (searchTerm && searchTerm !== null) {
        navigate(`/admin-portal/search?searchTerm=${searchTerm}`);
      }
    } else {
      if (searchTerm && searchTerm !== null) {
        navigate(`/search?searchTerm=${searchTerm}`);
      }
    }
    if (searchTerm !== queryData.search) {
      setQueryData({
        page: 1,
        category: "",
        search: searchTerm !== "" ? searchTerm : "",
      });
    }
  }, [loggedIn, navigate, queryData.search, searchTerm]);

  const { data: categories, isFetching: categoriesPending } =
    useGetCategoriesQuery({ search: searchTerm });
  const { data, isFetching, originalArgs } =
    useGetMarketplaceItemsQuery(queryData);

  const loadMoreItems = () => {
    if (!data?.maxReached && !isFetching) {
      setQueryData((prev) => {
        const page = originalArgs?.page + 1 ?? queryData.page + 1;
        return { ...prev, page };
      });
    }
  };

  const removeItemOpenModal = (id) => {
    setAreYouSureModal(true);
    setSelectedItem(id);
  };

  const removeItemAction = () => {
    dispatch(removeItem(selectedItem))
      .unwrap()
      .then(() => {
        setAreYouSureModal(false);

        toast.success(t("item-deleted"));

        const entries = api.util.selectInvalidatedBy(store.getState(), [
          "marketplaceItems",
        ]);
        for (const { originalArgs } of entries) {
          dispatch(
            api.endpoints.getMarketplaceItems.initiate(originalArgs, {
              subscribe: false,
              forceRefetch: true,
            })
          );
        }
      })
      .catch((err) => {
        openToast(toastId, "remove-item", "error", t(err.message));
      });
  };

  const handleItemClick = (itemId) => {
    dispatch(resetSelectedItem());
    dispatch(resetEditItemData());
    if (loggedIn) {
      navigate(`/admin-portal/marketplace/item-detail/${itemId}`);
    } else {
      navigate(`/marketplace/${itemId}`);
    }
  };
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollUpY, setLastScrollUpY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(null);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.scrollY;
      const lastScrollTop = lastScrollTopRef.current;

      if (currentScrollTop === 0) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
        setScrollDirection(null);
        lastScrollTopRef.current = currentScrollTop;
        return;
      }

      if (currentScrollTop > lastScrollTop) {
        setScrollDirection("down");
        setLastScrollUpY(currentScrollTop);
      } else {
        setScrollDirection("up");
      }

      if (scrollDirection === "up" && currentScrollTop < lastScrollUpY - 350) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
      }

      if (
        scrollDirection === "down" &&
        currentScrollTop > lastScrollUpY + 350
      ) {
        setHeaderVisible(false);
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    const handleScrollDebounced = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener("scroll", handleScrollDebounced);

    return () => {
      window.removeEventListener("scroll", handleScrollDebounced);
    };
  }, [scrollDirection]);

  return (
    <>
      {loggedIn ? (
        <>
          <SearchComponent search={searchTerm} />
          <SectionTitle $loggedIn={loggedIn}>
            {t("categories.categories")}
          </SectionTitle>

          {categoriesPending ? (
            <LoadingContainer>
              <AppLoader />
            </LoadingContainer>
          ) : categories?.data?.categories?.length > 0 ? (
            <SortContainer>
              {categories?.data?.categories.map((s) =>
                categories.loading ? (
                  <SkeletonMarketplaceSortLoader key={s.id} />
                ) : (
                  <SortName
                    key={s.id}
                    onClick={() => {
                      if (loggedIn) {
                        navigate(`/admin-portal/marketplace?category=${s.id}`);
                      } else {
                        navigate(`/marketplace?category=${s.id}`);
                      }
                    }}
                  >
                    {s.name}
                  </SortName>
                )
              )}
            </SortContainer>
          ) : (
            <NothingToShowContainer>
              <Subtitle style={{ marginTop: 0 }}>
                {t("nothing-to-show-here")}
              </Subtitle>
            </NothingToShowContainer>
          )}
          <SectionTitle $loggedIn={loggedIn}>{t("items")}</SectionTitle>

          {isFetching && queryData.page === 1 ? (
            <SkeletonMarketplaceLoader />
          ) : data?.data?.length > 0 ? (
            <StyledInfinityScroll
              // $admin={role === "superAdmin"}
              dataLength={data?.data?.length ?? 0}
              hasMore={!data?.maxReached}
              next={loadMoreItems}
              loader={<SkeletonMarketplaceLoader single />}
            >
              {data?.data?.map((product) => (
                <ProductCard
                  key={product.id}
                  reverse={product.favorite}
                  itemDetails={() => {
                    handleItemClick(product.id);
                  }}
                  product={product}
                  modal={() => {
                    setLoginModal(true);
                  }}
                  removeItem={removeItemOpenModal}
                />
              ))}
            </StyledInfinityScroll>
          ) : (
            <Subtitle style={{ marginTop: 0 }}>
              {t("nothing-to-show-here")}
            </Subtitle>
          )}

          {!loggedIn && <Footer />}
        </>
      ) : (
        <MainContainer $column>
          <HeaderWrapper
            style={{
              opacity: headerVisible ? 1 : 0,
              marginTop: headerVisible ? 0 : -150,
              transition: "all 0.3s ease-in-out",
            }}
          >
            <Header />
          </HeaderWrapper>
          <Content>
            <SearchComponent search={searchTerm} />
            <SectionTitle>{t("categories.categories")}</SectionTitle>

            {categoriesPending ? (
              <LoadingContainer>
                <AppLoader />
              </LoadingContainer>
            ) : categories?.data?.categories?.length > 0 ? (
              <SortContainer>
                {categories?.data?.categories.map((s) =>
                  categories.loading ? (
                    <SkeletonMarketplaceSortLoader key={s.id} />
                  ) : (
                    <SortName
                      key={s.id}
                      onClick={() => {
                        if (loggedIn) {
                          navigate(
                            `/admin-portal/marketplace?category=${s.id}`
                          );
                        } else {
                          navigate(`/marketplace?category=${s.id}`);
                        }
                      }}
                    >
                      {s.name}
                    </SortName>
                  )
                )}
              </SortContainer>
            ) : (
              <NothingToShowContainer>
                <Subtitle style={{ marginTop: 0 }}>
                  {t("nothing-to-show-here")}
                </Subtitle>
              </NothingToShowContainer>
            )}
            <SectionTitle $loggedIn={loggedIn}>{t("items")}</SectionTitle>
            {isFetching && queryData.page === 1 ? (
              <SkeletonMarketplaceLoader />
            ) : data?.data?.length > 0 ? (
              <StyledInfinityScroll
                // $admin={role === "superAdmin"}
                dataLength={data?.data?.length ?? 0}
                hasMore={!data?.maxReached}
                next={loadMoreItems}
                loader={<SkeletonMarketplaceLoader single />}
              >
                {data?.data?.map((product) => (
                  <ProductCard
                    key={product.id}
                    reverse={product.favorite}
                    itemDetails={() => {
                      handleItemClick(product.id);
                    }}
                    product={product}
                    modal={() => {
                      setLoginModal(true);
                    }}
                    removeItem={removeItemOpenModal}
                  />
                ))}
              </StyledInfinityScroll>
            ) : (
              <Subtitle style={{ marginTop: 0 }}>
                {t("nothing-to-show-here")}
              </Subtitle>
            )}
          </Content>

          <Footer />
        </MainContainer>
      )}

      <LoginModal open={loginModal} close={() => setLoginModal(false)} />
      <ModalConfirm
        open={areYouSureModal}
        close={() => {
          setAreYouSureModal(false);
        }}
        onClickNo={() => {
          setAreYouSureModal(false);
        }}
        onClickYes={() => {
          removeItemAction();
        }}
        confirmLoading={deletePending}
      />
    </>
  );
};

export default SearchPage;
